import Decimal from 'decimal.js';
import { compact } from 'lodash';

import { FUNCTIONAL } from '@/styles/tokens/colors';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';

import { EstateWaterfall_TransferLogItemFragment } from '../../../graphql/EstateWaterfall.generated';
import { EdgeLabelCard } from '../edgeLabelCard.component';
import { CardConfig } from './types';

interface GratFundingCardParams {
  sourceEntityName: string;
  targetEntityName: string;
  transfer?: EstateWaterfall_TransferLogItemFragment;
  associatedAnnuities: EstateWaterfall_TransferLogItemFragment[];
}

export function createGratFundingCard({
  sourceEntityName,
  targetEntityName,
  transfer,
  associatedAnnuities,
}: GratFundingCardParams): CardConfig {
  const grantor = sourceEntityName;
  const gratName = targetEntityName;

  const isHypothetical = transfer?.isHypothetical;

  // Get transfer amount
  const fundingAmount = transfer?.finalTransferAmount || new Decimal(0);

  const totalPayments = associatedAnnuities.reduce(
    (sum, payment) => sum.plus(payment.finalTransferAmount || 0),
    new Decimal(0)
  );

  const gratTerm = (() => {
    const grat = transfer?.associatedObject;
    if (!grat) {
      return null;
    }

    if (grat.__typename === 'HypotheticalGRAT') {
      return grat.simulatedGrat.gratTrust?.termDurationYears;
    }

    if (
      grat.__typename === 'Entity' &&
      grat.subtype.__typename === 'GRATTrust'
    ) {
      return grat.subtype.termDurationYears;
    }

    return null;
  })();

  const termEndDate = (() => {
    if (gratTerm) {
      return new Date().getFullYear() + gratTerm;
    }

    return null;
  })();

  return {
    element: (
      <EdgeLabelCard
        key={`grat-funding-${sourceEntityName}-${targetEntityName}`}
        title={isHypothetical ? 'Hypothetical GRAT funding' : 'GRAT funding'}
        titleSx={
          isHypothetical ? { color: FUNCTIONAL.WARNING[600] } : undefined
        }
        description={`${grantor} transfers assets to ${gratName}.`}
        rows={compact([
          {
            label: 'Funding amount',
            value: formatCurrencyNoDecimals(fundingAmount),
          },
          totalPayments.gt(0)
            ? {
                label: 'Total annuities',
                value: formatCurrencyNoDecimals(totalPayments),
              }
            : null,
          gratTerm
            ? {
                label: 'GRAT term',
                value: `${gratTerm} years`,
              }
            : null,
          termEndDate
            ? {
                label: 'Term end',
                value: termEndDate.toString(),
              }
            : null,
        ])}
      />
    ),
    label: 'GRAT funding',
    value: formatCurrencyNoDecimals(fundingAmount),
    variant: isHypothetical ? 'secondary' : 'primary',
  };
}
